import { navigate } from '../core/router';
import { esc } from '../ui/modal';
import { can } from '../core/access';
import { isHostlessFarm, isMercadoApiFarm } from '../core/farm-kind';
import { tabIcons } from '../ui/icons';
import { renderGeneralTab } from './general-tab';
import { renderAccessTab } from './access-tab';
import { renderUsersAccessTab } from './users-access-tab';
import { renderSetupTab } from './setup-tab';
import { renderHealthTab } from './farm-health-tab';
import { renderFarmTerminalTab } from './farm-terminal-tab';
import { renderFarmClickerConfig } from '../clicker/farm-clicker-config';
import { t } from '../ui/lang';
/**
 * Full-page farm settings view (replaces the old settings modal). Rendered for
 * the `#!/farms/<name>/settings` route. Admin-only. Each tab lives in its own
 * module ({general,access,launch,farm-health}-tab.ts); this view only wires the
 * tab strip and dispatches to those renderers.
 */
export async function renderFarmSettings(farmId) {
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('common.settings');
    // Resolve the farm record (name + relay key) by its stable id.
    let record;
    try {
        const r = await fetch('/api/farms');
        if (r.ok) {
            const list = await r.json();
            record = list.find((f) => f.id === farmId);
        }
    }
    catch { /* ignore */ }
    if (!record) {
        content.innerHTML = `
            <div class="farm-devs-header">
                <button class="back-btn" id="back-btn">${t('farms.back_to_farms')}</button>
            </div>
            <p class="empty">${t('farms.farm_not_found')}</p>
        `;
        document.getElementById('back-btn').addEventListener('click', () => navigate({ page: 'farms' }));
        return;
    }
    const farm = record;
    const farmName = farm.name;
    pageTitle.textContent = t('farms.settings_title', { name: farmName });
    // Agent-less farms (aggregator, duo, geelark) have no host machine / docker image, so
    // host-only tabs and actions (Terminal, docker Launch, reboot, image version) are
    // hidden. Each kind gets its own body in the shared "Setup" tab instead, and its
    // provider credentials (duo / geelark) are edited in "General".
    const hostless = isHostlessFarm(farm.farm_kind);
    // Senior operators reach this page via 'farm.deviceAccess' but may only distribute
    // devices — they see ONLY the Devices Access tab. Every other tab (Setup, General,
    // Users Access, Health, Terminal) is admin-only. `adminView` gates them all; when
    // false the page collapses to the single Devices Access tab.
    const adminView = can('farm.settings');
    const defaultTab = adminView ? 'setup' : 'access';
    // Per-farm clicker configuration (server, routing, Telegram, limits) — the same
    // form the per-farm Clicker page shows; admin and above.
    // Mercado API farms run no clicker — their server/bucket/poll interval live in General.
    const showClicker = adminView && can('clicker.config') && !isMercadoApiFarm(farm.farm_kind);
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="back-btn">← ${esc(farmName)}</button>
            <h2 class="settings-title">${t('farms.settings_title', { name: esc(farmName) })}</h2>
        </div>
        <div class="settings-tabs" role="tablist">
            ${adminView ? `<button class="settings-tab active" data-tab="setup">${tabIcons.launch}${t('farms.tab_setup')}</button>` : ''}
            ${adminView ? `<button class="settings-tab" data-tab="general">${tabIcons.general}${t('farms.tab_general')}</button>` : ''}
            ${showClicker ? `<button class="settings-tab" data-tab="clicker">${tabIcons.clicker}${t('farms.tab_clicker')}</button>` : ''}
            ${adminView ? `<button class="settings-tab" data-tab="users-access">${tabIcons.usersAccess}${t('farms.tab_users_access')}</button>` : ''}
            <button class="settings-tab${adminView ? '' : ' active'}" data-tab="access">${tabIcons.access}${t('farms.tab_devices_access')}</button>
            ${adminView && !hostless ? `<button class="settings-tab" data-tab="health">${tabIcons.health}${t('farms.tab_health')}</button>` : ''}
            ${adminView && can('farm.exec') && !hostless ? `<button class="settings-tab" data-tab="terminal">${tabIcons.terminal}${t('farms.tab_terminal')}</button>` : ''}
        </div>
        ${adminView ? '<div class="settings-panel" data-panel="setup"></div>' : ''}
        ${adminView ? '<div class="settings-panel" data-panel="general" style="display:none"></div>' : ''}
        ${showClicker ? '<div class="settings-panel" data-panel="clicker" style="display:none"></div>' : ''}
        ${adminView ? '<div class="settings-panel" data-panel="users-access" style="display:none"></div>' : ''}
        <div class="settings-panel" data-panel="access"${adminView ? ' style="display:none"' : ''}></div>
        ${adminView && !hostless ? '<div class="settings-panel" data-panel="health" style="display:none"></div>' : ''}
        ${adminView && can('farm.exec') && !hostless ? '<div class="settings-panel" data-panel="terminal" style="display:none"></div>' : ''}
    `;
    document.getElementById('back-btn').addEventListener('click', () => navigate({ page: 'farms', farmId }));
    const setupPanel = content.querySelector('[data-panel="setup"]');
    const generalPanel = content.querySelector('[data-panel="general"]');
    const clickerPanel = content.querySelector('[data-panel="clicker"]');
    const accessPanel = content.querySelector('[data-panel="access"]');
    const usersAccessPanel = content.querySelector('[data-panel="users-access"]');
    const healthPanel = content.querySelector('[data-panel="health"]');
    const terminalPanel = content.querySelector('[data-panel="terminal"]');
    const tabs = Array.from(content.querySelectorAll('.settings-tab'));
    const panels = Array.from(content.querySelectorAll('.settings-panel'));
    const showTab = (key) => {
        tabs.forEach((t) => t.classList.toggle('active', t.dataset['tab'] === key));
        panels.forEach((p) => { p.style.display = p.dataset['panel'] === key ? '' : 'none'; });
        // Device(s) Access and Users Access both derive from the farm's `user_farms`
        // grants, which either tab can change. Re-fetch/re-render on every activation
        // so switching between them never shows a stale matrix/operator list.
        if (key === 'access')
            void renderAccessTab(accessPanel, farm.id);
        else if (key === 'users-access' && usersAccessPanel)
            void renderUsersAccessTab(usersAccessPanel, farm.id);
    };
    tabs.forEach((t) => t.addEventListener('click', () => showTab(t.dataset['tab'])));
    if (adminView && generalPanel && setupPanel) {
        renderGeneralTab(generalPanel, farm);
        renderSetupTab(setupPanel, farm);
        if (clickerPanel)
            void renderFarmClickerConfig(clickerPanel, farm.id);
        if (!hostless && healthPanel)
            renderHealthTab(healthPanel, farm.id, farm.name);
        if (terminalPanel)
            renderFarmTerminalTab(terminalPanel, farm.id, farm.name);
    }
    // Lazily render whichever tab is active on first paint. For admins that's Setup
    // (rendered eagerly above); for senior operators it's the Devices Access matrix.
    showTab(defaultTab);
}
